import React, { useCallback, useEffect, useState } from 'react';

import { Alert, Chip, Link } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { Tour as TourResponse } from '@luxuryescapes/contract-svc-tour';

import { getTours } from '~/services/ToursService';
import { ReqInstance } from '~/services/types';

import { LETourSearchInput } from '../LETourSearchForm';

import SFTourRefreshButton from './SFTourRefreshButton';
import TourSellingRegionChips from './TourChips';
import TourScheduleIndicatorIcon from './TourScheduleIndicatorIcon';
import { getTourResultSellingRegions } from './tourSearchResultValueGetters';

const columns: Array<GridColDef<TourResponse.Tour>> = [
  {
    field: 'scheduleIndicator',
    headerName: '',
    align: 'center',
    display: 'flex',
    width: 40,
    valueGetter: (_value, row) => row.scheduleState?.state,
    renderCell: (params) => <TourScheduleIndicatorIcon status={params.value} />,
  },
  {
    field: 'name',
    headerName: 'Tour Name',
    display: 'flex',
    minWidth: 260,
    flex: 1,
    renderCell: (params) => (
      <Link href={`/tours/details/${params.row.id}`} target="_blank" className="le-tour-search-result-link">
        {params.value}
      </Link>
    ),
  },
  {
    field: 'brand',
    headerName: 'Operator',
    display: 'flex',
    maxWidth: 220,
    minWidth: 140,
    valueGetter: (_value, row) => row.brandObject.name,
  },
  {
    field: 'vendorName',
    headerName: 'Vendor',
    display: 'flex',
    maxWidth: 220,
    minWidth: 140,
  },
  {
    field: 'sellingRegion',
    headerName: 'Regions',
    maxWidth: 200,
    minWidth: 150,
    align: 'center',
    headerAlign: 'center',
    display: 'flex',
    valueGetter: (_value, row) => getTourResultSellingRegions(row),
    renderCell: (params) => <TourSellingRegionChips value={params.value} />,
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 140,
    align: 'center',
    headerAlign: 'center',
    display: 'flex',
    renderCell: (params) => (
      <Chip label={params.value} size="small" color={params.value === 'content-approved' ? 'info' : undefined} />
    ),
  },
  {
    field: 'id',
    headerName: 'ID',
    maxWidth: 240,
    minWidth: 200,
    display: 'flex',
  },
  {
    field: 'sourceTourId',
    headerName: 'SF ID',
    maxWidth: 240,
    minWidth: 200,
    display: 'flex',
  },
  {
    field: 'refreshAction',
    headerName: 'Refresh',
    width: 60,
    align: 'center',
    headerAlign: 'center',
    display: 'flex',
    renderCell: (params) => <SFTourRefreshButton salesforceId={params.row.sourceTourId} />,
  },
];

interface Props {
  searchInput: LETourSearchInput;
  pageIndex?: number;
  onPageChange: (pageIndex: number) => void;
}

const PAGE_SIZE = 20;

export default function LETourSearchResultsTable(props: Props) {
  const { searchInput, pageIndex, onPageChange } = props;
  const currentPageIndex = pageIndex ?? 0;
  const [toursFetchReq, setToursFetchReq] = useState<
    ReqInstance<{ tours: Array<TourResponse.Tour>; total: number; error?: string }>
  >({
    status: 'uninitiated',
  });
  const [rowCount, setRowCount] = useState(0);

  const fetchTours = useCallback(async (input: LETourSearchInput, reqPageIndex = 0) => {
    setToursFetchReq((prev) => ({
      status: 'pending',
      result: prev.status === 'succeeded' ? prev.result : undefined,
    }));

    try {
      const toursRes = await getTours({
        source: 'salesforce',
        page: String(reqPageIndex + 1),
        per_page: String(PAGE_SIZE),
        status: input.status,
        countriesVisited: input.countries,
        searchString: input.tourName,
        sourceTourIds: input.salesforceId,
        ids: input.ids,
      });

      const total = toursRes.result.total;
      const tours: Array<TourResponse.Tour> = toursRes.result.tours;
      const error = undefined;

      setToursFetchReq({ status: 'succeeded', result: { total, tours, error } });
      setRowCount(total);
    } catch (err) {
      setToursFetchReq({ status: 'failed', error: JSON.stringify(err) });
    }
  }, []);

  useEffect(() => {
    fetchTours(searchInput, currentPageIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, currentPageIndex]);

  const minimumRowCountForRequestedPage = currentPageIndex ? currentPageIndex * PAGE_SIZE + 1 : 0;
  const stableRowCount =
    toursFetchReq.status === 'succeeded' ? rowCount : Math.max(rowCount, minimumRowCountForRequestedPage);
  const rows = 'result' in toursFetchReq ? (toursFetchReq.result?.tours ?? []) : [];

  return (
    <>
      {toursFetchReq.status === 'failed' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {JSON.stringify(toursFetchReq.error)}
        </Alert>
      )}
      {toursFetchReq.status === 'succeeded' && toursFetchReq.result.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {toursFetchReq.result.error}
        </Alert>
      )}
      <DataGrid
        className="le-tour-search-results-table"
        columns={columns}
        rows={rows}
        rowCount={stableRowCount}
        getRowHeight={() => 'auto'}
        pagination
        paginationMode="server"
        paginationModel={{ page: currentPageIndex, pageSize: PAGE_SIZE }}
        pageSizeOptions={[PAGE_SIZE]}
        onPaginationModelChange={({ page }) => {
          if (page === currentPageIndex) return;
          if (toursFetchReq.status === 'pending' && page === 0 && currentPageIndex > 0) return;
          onPageChange(page);
        }}
        loading={toursFetchReq.status === 'pending'}
        autoHeight
        disableColumnFilter
        disableColumnMenu
      />
    </>
  );
}
